import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Empty, Input, InputNumber, Select, Switch, Table, Tag, Tooltip, Upload, message } from "antd";
import {
    CheckCircleFilled, CopyOutlined, DeleteOutlined, DownloadOutlined, ReloadOutlined, SaveOutlined, SearchOutlined,
    UploadOutlined,
} from "@ant-design/icons";
import { t } from "i18next";
import ApiService from "../../core/service/api-service";
import { copyToClipboard } from "../../core/utils/clipboard-utils";
import "./config.css";

/**
 * appsettings.json yönetim paneli.
 *
 * Form ŞEMADAN üretilir (backend'deki ConfigDoc nitelikleri): yeni bir ayar eklendiğinde burada
 * kod değiştirmeye gerek yoktur, alan kendiliğinden görünür. Böylece "ayar eklendi ama arayüze
 * konmadı, kimse bilmiyor" durumu doğmaz.
 *
 * Görünüm sınıf yapısına (App, HL7…) değil ŞEMADAKİ kategoriye göredir: soldaki menü kategoriler,
 * sağda her bölüm / alt grup bir kart. Başlık (Label), gösterim koşulu (DependsOn) ve kategori
 * (Category) ConfigDoc'tan gelir; appsettings.json'daki anahtarlar değişmez.
 *
 * Notlar:
 *  - DB bağlantıları bu panelde YOKTUR: backend yalnız RootObject bölümlerini yazar, ConnectionStrings
 *    dosyada olduğu gibi korunur.
 *  - Gizli alanların değeri sunucudan hiç gelmez ("***" = ayarlı). Yeni değer yazılmadıkça eski sır korunur.
 *  - Bazı ayarlar açılışta sabitlenir (RequiresRestart) → kaydetmek yetmez, uyarı gösterilir.
 */
export default function ConfigPanel()
{
    const [sections, setSections] = useState([]);
    const [changes, setChanges] = useState({});     // { "App.JwtKey": value }
    const [saving, setSaving] = useState(false);
    const [restartNeeded, setRestartNeeded] = useState([]);
    const [activeCategory, setActiveCategory] = useState(null);
    const [query, setQuery] = useState("");

    const load = async () => {
        const res = await ApiService.Config.GetSchema();
        if (res?.success) {
            setSections(res.data || []);
            setChanges({});
        }
    };

    useEffect(() => {
        load();
    }, []);

    const categories = useMemo(() => buildCategories(sections), [sections]);
    const leaves = useMemo(() => flattenLeaves(sections), [sections]);

    const currentCategory = categories.find((c) => c.key === activeCategory) || categories[0];

    const valueOf = (key) =>
        Object.prototype.hasOwnProperty.call(changes, key) ? changes[key] : leaves[key]?.value;

    const isVisible = (node) => conditionMet(node.dependsOn, valueOf);

    const dirtyCount = Object.keys(changes).length;

    const setValue = (key, value) => setChanges((prev) => ({ ...prev, [key]: value }));

    const resetValue = (key) => setChanges((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
    });

    const save = async () => {
        const updates = Object.entries(changes).map(([key, value]) => ({ key, value }));
        if (updates.length === 0) return;

        setSaving(true);
        const res = await ApiService.Config.SaveSettings(updates);
        setSaving(false);

        if (res?.success) {
            setRestartNeeded(res.data?.requiresRestart ? (res.data?.restartKeys || []) : []);
            await load();
        }
    };

    const fieldProps = { changes, valueOf, onChange: setValue, onReset: resetValue };
    const searching = query.trim().length > 0;
    const results = searching ? searchCategories(categories, query.trim().toLocaleLowerCase("tr")) : [];

    return (
        <div className="config-panel">
            <div className="config-toolbar">
                <div className="config-intro">
                    {t("Settings live in appsettings.json on the server. Saving here rewrites that file and reloads the configuration — database connections are not editable.")}
                </div>
                <div className="config-actions">
                    <Input
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder={t("Search settings")}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="config-search"
                    />
                    <Button icon={<ReloadOutlined />} onClick={load}>{t("Reload")}</Button>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={saving}
                        disabled={dirtyCount === 0}
                        onClick={save}
                    >
                        {t("Save")}{dirtyCount > 0 ? ` (${dirtyCount})` : ""}
                    </Button>
                </div>
            </div>

            {restartNeeded.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    className="config-restart"
                    message={t("Saved. Some settings only take effect after a restart:")}
                    description={restartNeeded.map((key) => leaves[key]?.label || key).join(", ")}
                    closable
                    onClose={() => setRestartNeeded([])}
                />
            )}

            <div className="config-layout">
                <nav className="config-nav">
                    {categories.map((category) => {
                        const dirty = Object.keys(changes).filter((key) => leaves[key]?.category === category.key).length;
                        const active = !searching && category.key === currentCategory?.key;
                        return (
                            <button
                                type="button"
                                key={category.key}
                                className={`config-nav-item ${active ? "is-active" : ""}`}
                                onClick={() => { setActiveCategory(category.key); setQuery(""); }}
                            >
                                <span>{category.label}</span>
                                {dirty > 0 && <Tag className="config-tag-dirty">{dirty}</Tag>}
                            </button>
                        );
                    })}
                </nav>

                <div className="config-content">
                    {searching
                        ? (results.length === 0
                            ? <Empty description={t("No matching setting")} />
                            : results.map((category) => (
                                <section key={category.key} className="config-search-group">
                                    <h3 className="config-category-title">{category.label}</h3>
                                    {category.cards.map((card) => (
                                        <ConfigCard key={card.key} card={card} showTitle plain isVisible={() => true} {...fieldProps} />
                                    ))}
                                </section>
                            )))
                        : currentCategory && (
                            <>
                                <h3 className="config-category-title">{currentCategory.label}</h3>
                                {currentCategory.cards.map((card) => (
                                    <ConfigCard
                                        key={card.key}
                                        card={card}
                                        showTitle={currentCategory.cards.length > 1}
                                        isVisible={isVisible}
                                        {...fieldProps}
                                    />
                                ))}
                            </>
                        )}
                </div>
            </div>
        </div>
    );
}

/**
 * Şemayı menü kategorilerine ve kartlara çevirir. Bölümün doğrudan ayarları (yaprakları) kategori
 * başına tek kartta toplanır (başlık: bölüm adı); alt nesneler (App.Sms, App.Mail) kendi kartıdır.
 * Kart ve kategori sırası şemadaki sıradır; kategoriler backend'deki enum sırasıyla dizilir.
 */
function buildCategories(sections)
{
    const categories = new Map();

    const categoryOf = (node) => {
        if (!categories.has(node.category)) {
            categories.set(node.category, { key: node.category, label: node.categoryLabel, order: node.categoryOrder, cards: [] });
        }
        return categories.get(node.category);
    };

    for (const section of sections) {
        const leafCards = new Map();

        for (const node of section.children || []) {
            const category = categoryOf(node);

            if (node.children?.length) {
                category.cards.push({ key: node.key, title: nodeLabel(node), description: node.description, nodes: node.children });
                continue;
            }

            const cardKey = `${section.key}@${node.category}`;
            if (!leafCards.has(cardKey)) {
                const card = { key: cardKey, title: nodeLabel(section), description: section.description, nodes: [] };
                leafCards.set(cardKey, card);
                category.cards.push(card);
            }
            leafCards.get(cardKey).nodes.push(node);
        }
    }

    return [...categories.values()].sort((a, b) => a.order - b.order);
}

/** Tüm yapraklar anahtara göre — bağımlılık koşulu ve kategori sayacı için. */
function flattenLeaves(nodes, into = {})
{
    for (const node of nodes) {
        if (node.children?.length) flattenLeaves(node.children, into);
        else into[node.key] = node;
    }
    return into;
}

/** Aramada kartları eşleşen yapraklara indirger; boş kalan kart ve kategori düşer. */
function searchCategories(categories, needle)
{
    const matches = (node) => [node.label, node.name, node.description, node.key]
        .some((text) => text && text.toLocaleLowerCase("tr").includes(needle));

    const filterNodes = (nodes) => nodes.flatMap((node) => {
        if (!node.children?.length) return matches(node) ? [node] : [];
        const children = filterNodes(node.children);
        return children.length ? [{ ...node, children }] : [];
    });

    return categories
        .map((category) => ({
            ...category,
            cards: category.cards
                .map((card) => ({ ...card, nodes: filterNodes(card.nodes) }))
                .filter((card) => card.nodes.length > 0),
        }))
        .filter((category) => category.cards.length > 0);
}

/**
 * ConfigDoc.DependsOn: "App.EnableStudyTokenService" → o bool açıkken,
 * "App.Mail.Service=Smtp|Postacell" → değer listedekilerden biriyken. Koşulsuz alan hep görünür.
 */
function conditionMet(dependsOn, valueOf)
{
    if (!dependsOn) return true;

    const [key, expected] = dependsOn.split("=");
    const value = valueOf(key.trim());

    if (expected === undefined) return value === true || value === "true";

    const actual = String(value ?? "").toLowerCase();
    return expected.split("|").some((v) => v.trim().toLowerCase() === actual);
}

function nodeLabel(node)
{
    return node.label || node.name;
}

/**
 * Kartın ilk ayarı bir aç/kapa ve diğer TÜM ayarlar ona bağlıysa (Vtr.Enabled gibi), o anahtar kartın
 * başlığına çıkar: kart kapalıyken yalnız başlık görünür.
 */
function masterSwitchOf(card)
{
    const [first, ...rest] = card.nodes;
    if (!first || first.children?.length || first.type !== "bool" || rest.length === 0) return null;
    return rest.every((node) => node.dependsOn === first.key) ? first : null;
}

function ConfigCard({ card, showTitle, plain, isVisible, changes, valueOf, onChange, onReset })
{
    const master = plain ? null : masterSwitchOf(card);
    const nodes = master ? card.nodes.slice(1) : card.nodes;
    const masterDirty = master && Object.prototype.hasOwnProperty.call(changes, master.key);
    const fieldProps = { changes, valueOf, onChange, onReset, isVisible };

    return (
        <div className="config-card">
            {(showTitle || master) && (
                <div className={`config-card-head ${masterDirty ? "is-dirty" : ""}`}>
                    <div className="config-card-heading">
                        <div className="config-card-title">
                            {card.title}
                            {master?.requiresRestart && <RestartTag />}
                        </div>
                        {(master?.description || card.description) && (
                            <div className="config-card-desc">{master?.description || card.description}</div>
                        )}
                    </div>
                    {master && (
                        <Switch
                            checked={isTrue(valueOf(master.key))}
                            onChange={(v) => onChange(master.key, v)}
                        />
                    )}
                </div>
            )}

            <FieldList nodes={nodes} {...fieldProps} />
        </div>
    );
}

function FieldList({ nodes, isVisible, ...fieldProps })
{
    return (
        <div className="config-fields">
            {nodes.filter(isVisible).map((node) =>
                node.children?.length
                    ? (
                        <div className="config-group" key={node.key}>
                            <div className="config-group-title">{nodeLabel(node)}</div>
                            <FieldList nodes={node.children} isVisible={isVisible} {...fieldProps} />
                        </div>
                    )
                    : <ConfigField key={node.key} node={node} {...fieldProps} />
            )}
        </div>
    );
}

function ConfigField({ node, changes, valueOf, onChange, onReset })
{
    const dirty = Object.prototype.hasOwnProperty.call(changes, node.key);
    const value = valueOf(node.key);
    const label = nodeLabel(node);

    return (
        <div className={`config-field ${dirty ? "is-dirty" : ""}`}>
            <div className="config-field-label">
                <div className="config-field-name">
                    {label}
                    {node.requiresRestart && <RestartTag />}
                    <Tooltip title={`${node.key} — ${t("Copy key")}`}>
                        <button
                            type="button"
                            className="config-field-copy"
                            onClick={() => copyToClipboard(node.key)}
                        >
                            <CopyOutlined />
                        </button>
                    </Tooltip>
                </div>
                {node.description && node.description !== label && (
                    <div className="config-field-desc">{node.description}</div>
                )}
            </div>

            <div className="config-field-control">
                {node.secret
                    ? <SecretControl node={node} dirty={dirty} value={value} onChange={(v) => onChange(node.key, v)} onReset={() => onReset(node.key)} />
                    : <Control node={node} value={value} onChange={(v) => onChange(node.key, v)} />}
                {(node.kind === "file" || node.kind === "directory") && <FileControl node={node} />}
            </div>
        </div>
    );
}

function RestartTag()
{
    return (
        <Tooltip title={t("Takes effect after restart")}>
            <Tag className="config-tag-restart">{t("restart")}</Tag>
        </Tooltip>
    );
}

function isTrue(value)
{
    return value === true || value === "true";
}

/**
 * Gizli alan: değer sunucudan hiç gelmez, yalnız ayarlı mı ("***") değil mi bilinir. Ayarlıysa
 * "Değiştir" ile yeni değer yazılır; boş bırakılan düzenleme değişiklik sayılmaz (eski sır korunur).
 */
function SecretControl({ node, dirty, value, onChange, onReset })
{
    const isSet = node.value === "***";
    const [editing, setEditing] = useState(false);

    // Kaydet/Yenile şemayı yeniden yükler (yeni node nesnesi) → düzenleme kapanır.
    useEffect(() => {
        setEditing(false);
    }, [node]);

    if (isSet && !editing) {
        return (
            <span className="config-secret-state">
                <CheckCircleFilled className="config-secret-ok" />
                {t("Set")}
                <Button size="small" type="link" onClick={() => setEditing(true)}>{t("Change")}</Button>
            </span>
        );
    }

    return (
        <span className="config-secret-edit">
            <Input.Password
                size="small"
                value={dirty ? value : ""}
                onChange={(e) => (e.target.value ? onChange(e.target.value) : onReset())}
                placeholder={isSet ? t("New value") : t("Not set")}
                autoFocus={editing}
                className="config-input"
            />
            {isSet && (
                <Button size="small" type="link" onClick={() => { onReset(); setEditing(false); }}>{t("Cancel")}</Button>
            )}
        </span>
    );
}

function Control({ node, value, onChange })
{
    if (node.type === "bool") {
        return <Switch size="small" checked={isTrue(value)} onChange={onChange} />;
    }

    if (node.allowedValues?.length) {
        return (
            <Select
                size="small"
                value={value ?? undefined}
                onChange={onChange}
                options={node.allowedValues.map((v) => ({ value: v, label: String(v) }))}
                className="config-input"
                allowClear
            />
        );
    }

    if (node.type === "int" || node.type === "number") {
        return <InputNumber size="small" value={value} onChange={onChange} className="config-input-num" />;
    }

    if (node.type === "list" && (value == null || (Array.isArray(value) && value.every((v) => typeof v === "string")))) {
        // Metin listesi (ör. CORS origin'leri): her değer bir etiket; Enter / virgül ile eklenir.
        return (
            <Select
                size="small"
                mode="tags"
                value={value || []}
                onChange={onChange}
                open={false}
                tokenSeparators={[",", " "]}
                suffixIcon={null}
                className="config-input"
            />
        );
    }

    if (node.type === "list" || node.type === "map") {
        // Sözlük ve karmaşık listeler JSON olarak düzenlenir (ör. Storage.Map).
        return (
            <Input.TextArea
                autoSize={{ minRows: 1, maxRows: 6 }}
                value={typeof value === "string" ? value : JSON.stringify(value ?? (node.type === "list" ? [] : {}), null, 2)}
                onChange={(e) => onChange(e.target.value)}
                className="config-input config-input-json"
            />
        );
    }

    return (
        <Input
            size="small"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="config-input"
        />
    );
}

/** Dosya tipli ayar: içeriği yükle/indir. Klasör tipli ayar: içine dosya at, listele, sil. */
function FileControl({ node })
{
    const isDirectory = node.kind === "directory";
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);   // klasör içeriği istenmedikçe kapalı — yer kaplamasın

    const loadFiles = async () => {
        if (!isDirectory || !node.value) return;
        setLoading(true);
        const res = await ApiService.Config.ListFiles(node.key);
        setLoading(false);
        if (res?.success) setFiles(res.data || []);
    };

    useEffect(() => {
        if (open) loadFiles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node.key, node.value, open]);

    const upload = async (file) => {
        const res = await ApiService.Config.UploadFile(node.key, file);
        if (res?.success) {
            message.success(t("Uploaded"));
            await loadFiles();
        }
        return false;   // antd'nin kendi isteğini engelle
    };

    const remove = async (name) => {
        const res = await ApiService.Config.DeleteFile(node.key, name);
        if (res?.success) await loadFiles();
    };

    return (
        <div className="config-file">
            <div className="config-file-actions">
                <Upload showUploadList={false} beforeUpload={upload}>
                    <Tooltip title={isDirectory ? t("Upload into folder") : t("Upload file")}>
                        <Button size="small" type="text" icon={<UploadOutlined />} disabled={!node.value} />
                    </Tooltip>
                </Upload>

                {!isDirectory && (
                    <Tooltip title={t("Download")}>
                        <Button
                            size="small"
                            type="text"
                            icon={<DownloadOutlined />}
                            disabled={!node.value}
                            href={ApiService.Config.DownloadFileUrl(node.key)}
                            target="_blank"
                        />
                    </Tooltip>
                )}

                {isDirectory && node.value && (
                    <Button size="small" type="link" className="config-file-toggle" onClick={() => setOpen((v) => !v)}>
                        {open ? t("Hide files") : t("Files")}
                    </Button>
                )}

                {!node.value && (
                    <span className="config-hint">{t("Set the path and save first.")}</span>
                )}
            </div>

            {isDirectory && node.value && open && (
                <Table
                    size="small"
                    rowKey="name"
                    loading={loading}
                    dataSource={files}
                    pagination={files.length > 10 ? { pageSize: 10 } : false}
                    className="config-file-table"
                    columns={[
                        { title: t("File"), dataIndex: "name" },
                        {
                            title: t("Size"),
                            dataIndex: "size",
                            width: 110,
                            render: (size) => `${(size / 1024).toFixed(0)} KB`,
                        },
                        {
                            title: "",
                            width: 90,
                            render: (_, row) => (
                                <span className="config-file-row-actions">
                                    <a href={ApiService.Config.DownloadFileUrl(node.key, row.name)} target="_blank" rel="noreferrer">
                                        <DownloadOutlined />
                                    </a>
                                    <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => remove(row.name)} />
                                </span>
                            ),
                        },
                    ]}
                />
            )}
        </div>
    );
}
